/**
 * BootScreen - Title screen with config and board/sprint selection
 * Flow: TITLE → CONFIG (if needed) → BOARDS → SPRINTS → LOADING
 */

import { useState, useEffect } from 'react';
import { useGame } from '../context/GameContext';
import { ApiService } from '../services/ApiService';
import type { Board, Sprint } from '../types/game';
import './BootScreen.css';

type BootStep = 'TITLE' | 'CONFIG' | 'CONFIG_SP' | 'BOARDS' | 'SPRINTS';

interface JiraConfig {
  jiraHost: string;
  jiraEmail: string;
  jiraToken: string;
  storyPointsField: string;
  storyPointsFieldName: string;
  configuredAt: string;
}

interface JiraField {
  id: string;
  name: string;
  schema?: {
    type: string;
  };
}

const STORAGE_KEY = 'easydaily_config';

export function BootScreen() {
  const { state, actions } = useGame();
  const { displayMode } = state;
  const [step, setStep] = useState<BootStep>('TITLE');
  const [boards, setBoards] = useState<Board[]>([]);
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [selectedBoard, setSelectedBoard] = useState<Board | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Config form state
  const [jiraHost, setJiraHost] = useState('');
  const [jiraEmail, setJiraEmail] = useState('');
  const [jiraToken, setJiraToken] = useState('');
  const [connectionTested, setConnectionTested] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // Story Points detection state
  const [storyPointFields, setStoryPointFields] = useState<JiraField[]>([]);
  const [selectedSpField, setSelectedSpField] = useState<string>('');
  const [detectedSpField, setDetectedSpField] = useState<JiraField | null>(null);

  // Load existing config
  const getStoredConfig = (): JiraConfig | null => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  };

  // Save config
  const saveConfig = (config: JiraConfig) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  };

  // Clear config
  const clearConfig = () => {
    localStorage.removeItem(STORAGE_KEY);
  };

  // Fetch boards when entering BOARDS step
  useEffect(() => {
    if (step === 'BOARDS' && boards.length === 0) {
      setLoading(true);
      setError(null);
      ApiService.getBoards()
        .then(setBoards)
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }
  }, [step, boards.length]);

  // Handle PRESS START
  const handleStart = async () => {
    setLoading(true);
    setError(null);

    try {
      // Try to fetch boards - this works if:
      // 1. Backend has .env configured (local mode)
      // 2. localStorage has config (cloud mode with prior setup)
      const fetchedBoards = await ApiService.getBoards();
      setBoards(fetchedBoards);
      setStep('BOARDS');
    } catch {
      // If fetch fails (401 or no backend), show config wizard
      const config = getStoredConfig();
      if (config) {
        // Had config but it failed - maybe token expired
        setConnectionError('Connection failed. Please check your credentials.');
        setJiraHost(config.jiraHost);
        setJiraEmail(config.jiraEmail);
        setJiraToken(config.jiraToken);
      }
      setStep('CONFIG');
    } finally {
      setLoading(false);
    }
  };

  // Handle settings click (to reconfigure)
  const handleSettingsClick = () => {
    const config = getStoredConfig();
    if (config) {
      setJiraHost(config.jiraHost);
      setJiraEmail(config.jiraEmail);
      setJiraToken(config.jiraToken);
    }
    setConnectionTested(false);
    setConnectionError(null);
    setStep('CONFIG');
  };

  // Handle back to cartridge selection
  const handleBackToCartridge = () => {
    localStorage.removeItem('selectedProvider');
    actions.setPhase('CARTRIDGE_SELECT');
  };

  // Test Jira connection
  const handleTestConnection = async () => {
    setLoading(true);
    setConnectionError(null);
    setConnectionTested(false);

    // Normalize host
    let normalizedHost = jiraHost.trim();
    if (!normalizedHost.startsWith('http')) {
      normalizedHost = `https://${normalizedHost}`;
    }
    if (normalizedHost.endsWith('/')) {
      normalizedHost = normalizedHost.slice(0, -1);
    }
    setJiraHost(normalizedHost);

    // Temporarily save config for API call
    const tempConfig: JiraConfig = {
      jiraHost: normalizedHost,
      jiraEmail: jiraEmail.trim(),
      jiraToken: jiraToken.trim(),
      storyPointsField: '',
      storyPointsFieldName: '',
      configuredAt: new Date().toISOString(),
    };
    saveConfig(tempConfig);

    try {
      // Try to fetch boards to test connection
      await ApiService.getBoards();
      setConnectionTested(true);
    } catch (err) {
      setConnectionError(err instanceof Error ? err.message : 'Connection failed');
      clearConfig(); // Remove temp config on failure
    } finally {
      setLoading(false);
    }
  };

  // Go to Story Points detection step
  const handleNextToSpDetection = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch fields to detect story points
      const fields = await ApiService.getFields();

      // Filter custom fields that look like story points
      const candidates = fields.filter((f: JiraField) =>
        f.id.startsWith('customfield_') &&
        (f.schema?.type === 'number' || f.name.toLowerCase().includes('point'))
      );

      setStoryPointFields(candidates);

      // Try to auto-detect
      const detected = candidates.find((f: JiraField) =>
        f.name.toLowerCase().includes('story point') ||
        f.name.toLowerCase().includes('estimation') ||
        f.name.toLowerCase() === 'sp' ||
        f.name.toLowerCase() === 'points'
      );

      if (detected) {
        setDetectedSpField(detected);
        setSelectedSpField(detected.id);
      } else if (candidates.length > 0) {
        setSelectedSpField(candidates[0].id);
      }

      setStep('CONFIG_SP');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch fields');
    } finally {
      setLoading(false);
    }
  };

  // Complete configuration
  const handleCompleteConfig = () => {
    const selectedField = storyPointFields.find(f => f.id === selectedSpField);

    const config: JiraConfig = {
      jiraHost,
      jiraEmail,
      jiraToken,
      storyPointsField: selectedSpField,
      storyPointsFieldName: selectedField?.name || '',
      configuredAt: new Date().toISOString(),
    };

    saveConfig(config);
    setStep('BOARDS');
  };

  // Handle board selection
  const handleSelectBoard = async (board: Board) => {
    setSelectedBoard(board);
    actions.selectBoard(board);
    setLoading(true);
    setError(null);

    try {
      const fetchedSprints = await ApiService.getSprints(board.id);
      setSprints(fetchedSprints);
      setStep('SPRINTS');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch sprints');
    } finally {
      setLoading(false);
    }
  };

  // Handle sprint selection
  const handleSelectSprint = (sprint: Sprint) => {
    actions.selectSprint(sprint);
    actions.startDaily();
    actions.setPhase('LOADING');
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle keyboard shortcuts when typing in an input field
      const target = e.target as HTMLElement;
      const isInputField = target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA';

      if (step === 'TITLE' && (e.code === 'Enter' || e.code === 'Space')) {
        handleStart();
      }

      // Only allow Escape for back navigation (not B key) when in input fields
      // And only use KeyB shortcut when not in an input field
      if (e.code === 'Escape' || (!isInputField && e.code === 'KeyB')) {
        if (step === 'SPRINTS') {
          setStep('BOARDS');
          setSprints([]);
        } else if (step === 'BOARDS') {
          setStep('TITLE');
        } else if (step === 'CONFIG_SP') {
          setStep('CONFIG');
        } else if (step === 'CONFIG') {
          setStep('TITLE');
          setConnectionTested(false);
          setConnectionError(null);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [step]);

  // Settings icon component
  const SettingsIcon = () => (
    <button className="settings-icon" onClick={handleSettingsClick} title="Jira Settings">
      ⚙️
    </button>
  );

  // Render TITLE step
  if (step === 'TITLE') {
    return (
      <div className="screen boot-screen">
        {/* Pixel Art Sprites */}
        <div className="pixel-sprites">
          <div className="pixel-sprite sprite-knight" />
          <div className="pixel-sprite sprite-mage" />
          <div className="pixel-sprite sprite-coin sprite-coin-1" />
          <div className="pixel-sprite sprite-coin sprite-coin-2" />
          <div className="pixel-sprite sprite-coin sprite-coin-3" />
          <div className="pixel-sprite sprite-heart sprite-heart-1" />
          <div className="pixel-sprite sprite-heart sprite-heart-2" />
          <div className="pixel-sprite sprite-star sprite-star-1" />
          <div className="pixel-sprite sprite-star sprite-star-2" />
          <div className="pixel-sprite sprite-star sprite-star-3" />
          <div className="pixel-sprite sprite-star sprite-star-4" />
          <div className="pixel-sprite sprite-cloud sprite-cloud-1" />
          <div className="pixel-sprite sprite-cloud sprite-cloud-2" />
          <div className="pixel-sprite sprite-cloud sprite-cloud-3" />
        </div>

        <div className="boot-logo">
          <h1 className="boot-title">EASY</h1>
          <h1 className="boot-title boot-title--quest">DAILY</h1>
        </div>
        <p className="boot-subtitle">GAMIFIED STANDUP</p>
        <button
          className="boot-start blink"
          onClick={handleStart}
          autoFocus
        >
          PRESS START
        </button>

        {/* Display mode toggle */}
        <div className="boot-mode-toggle">
          <span className="boot-mode-label">DISPLAY MODE</span>
          <div className="boot-mode-buttons">
            <button
              className={`boot-mode-btn ${displayMode === 'kanban' ? 'active' : ''}`}
              onClick={() => actions.setDisplayMode('kanban')}
            >
              KANBAN
            </button>
            <button
              className={`boot-mode-btn ${displayMode === 'list' ? 'active' : ''}`}
              onClick={() => actions.setDisplayMode('list')}
            >
              LIST
            </button>
          </div>
        </div>

        <button
          className="back-to-cartridge"
          onClick={handleBackToCartridge}
        >
          [B] CHANGE CARTRIDGE
        </button>

        <div className="boot-footer">
          <span>fxbravo with ❤️</span>
        </div>
      </div>
    );
  }

  // Render loading state
  if (loading) {
    return (
      <div className="screen boot-screen">
        <div className="loading-spinner" />
        <p className="subtitle">Loading...</p>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="screen boot-screen">
        <h2 className="title" style={{ color: 'var(--color-error)' }}>ERROR</h2>
        <p className="subtitle">{error}</p>
        <button
          className="pixel-button"
          onClick={() => {
            setError(null);
            setStep('TITLE');
          }}
        >
          RETRY
        </button>
      </div>
    );
  }

  // Render CONFIG step
  if (step === 'CONFIG') {
    return (
      <div className="screen boot-screen">
        <h2 className="title">JIRA CONFIG</h2>
        <p className="subtitle">Step 1/2 - Connection</p>

        <div className="config-form">
          <div className="config-field">
            <label className="config-label">Jira URL</label>
            <input
              type="text"
              className="config-input"
              placeholder="acme.atlassian.net"
              value={jiraHost}
              onChange={(e) => {
                setJiraHost(e.target.value);
                setConnectionTested(false);
              }}
              autoFocus
            />
          </div>

          <div className="config-field">
            <label className="config-label">Email</label>
            <input
              type="email"
              className="config-input"
              placeholder="you@company.com"
              value={jiraEmail}
              onChange={(e) => {
                setJiraEmail(e.target.value);
                setConnectionTested(false);
              }}
            />
          </div>

          <div className="config-field">
            <label className="config-label">API Token</label>
            <input
              type="password"
              className="config-input"
              placeholder="••••••••••••"
              value={jiraToken}
              onChange={(e) => {
                setJiraToken(e.target.value);
                setConnectionTested(false);
              }}
            />
            <a
              href="https://id.atlassian.com/manage-profile/security/api-tokens"
              target="_blank"
              rel="noopener noreferrer"
              className="config-help-link"
            >
              💡 How to get a token?
            </a>
          </div>

          {connectionError && (
            <div className="config-error">
              ❌ {connectionError}
            </div>
          )}

          {connectionTested && (
            <div className="config-success">
              ✅ Connection successful!
            </div>
          )}

          <div className="config-actions">
            {!connectionTested ? (
              <button
                className="pixel-button"
                onClick={handleTestConnection}
                disabled={!jiraHost || !jiraEmail || !jiraToken}
              >
                TEST CONNECTION
              </button>
            ) : (
              <button
                className="pixel-button pixel-button--primary"
                onClick={handleNextToSpDetection}
              >
                NEXT →
              </button>
            )}
          </div>
        </div>

        <div className="boot-controls">
          <span>[B] Back</span>
        </div>
      </div>
    );
  }

  // Render CONFIG_SP step (Story Points detection)
  if (step === 'CONFIG_SP') {
    return (
      <div className="screen boot-screen">
        <h2 className="title">STORY POINTS</h2>
        <p className="subtitle">Step 2/2 - Field Detection</p>

        <div className="config-form">
          {detectedSpField ? (
            <div className="config-detected">
              <span className="config-detected-icon">✅</span>
              <span>Detected: <strong>{detectedSpField.name}</strong></span>
            </div>
          ) : (
            <div className="config-detected config-detected--warning">
              <span className="config-detected-icon">⚠️</span>
              <span>Could not auto-detect. Please select:</span>
            </div>
          )}

          <div className="config-field">
            <label className="config-label">Story Points Field</label>
            <select
              className="config-select"
              value={selectedSpField}
              onChange={(e) => setSelectedSpField(e.target.value)}
            >
              {storyPointFields.map((field) => (
                <option key={field.id} value={field.id}>
                  {field.name} ({field.id})
                </option>
              ))}
            </select>
          </div>

          <div className="config-actions">
            <button
              className="pixel-button pixel-button--primary"
              onClick={handleCompleteConfig}
              disabled={!selectedSpField}
            >
              LET'S GO! 🎰
            </button>
          </div>
        </div>

        <div className="boot-controls">
          <span>[B] Back</span>
        </div>
      </div>
    );
  }

  // Filter boards based on search query
  const filteredBoards = boards.filter((board) =>
    board.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    board.location?.projectKey?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Render BOARDS step
  if (step === 'BOARDS') {
    return (
      <div className="screen boot-screen">
        <SettingsIcon />
        <h2 className="title">SELECT SQUAD</h2>

        {/* Search input */}
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Search boards..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
          {searchQuery && (
            <button
              className="search-clear"
              onClick={() => setSearchQuery('')}
            >
              ✕
            </button>
          )}
        </div>

        <div className="boot-list">
          {filteredBoards.length === 0 ? (
            <p className="subtitle">
              {boards.length === 0 ? 'No boards found' : 'No matching boards'}
            </p>
          ) : (
            filteredBoards.map((board) => (
              <button
                key={board.id}
                className="boot-list-item"
                onClick={() => handleSelectBoard(board)}
              >
                <span className="boot-list-icon">▸</span>
                <span className="boot-list-name">{board.name}</span>
                {board.location && (
                  <span className="boot-list-project">
                    {board.location.projectKey}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
        <div className="boot-controls">
          <span>[B] Back</span>
          <span>[ENTER] Select</span>
        </div>
      </div>
    );
  }

  // Render SPRINTS step
  if (step === 'SPRINTS') {
    return (
      <div className="screen boot-screen">
        <SettingsIcon />
        <h2 className="title">SELECT SPRINT</h2>
        <p className="subtitle">{selectedBoard?.name}</p>
        <div className="boot-list">
          {sprints.length === 0 ? (
            <p className="subtitle">No active sprints found</p>
          ) : (
            sprints.map((sprint) => (
              <button
                key={sprint.id}
                className="boot-list-item"
                onClick={() => handleSelectSprint(sprint)}
              >
                <span className="boot-list-icon">▸</span>
                <span className="boot-list-name">{sprint.name}</span>
                <span className={`boot-list-state boot-list-state--${sprint.state}`}>
                  {sprint.state.toUpperCase()}
                </span>
              </button>
            ))
          )}
        </div>
        <div className="boot-controls">
          <span>[B] Back</span>
          <span>[ENTER] Select</span>
        </div>
      </div>
    );
  }

  return null;
}
